<script lang="ts">
  import { onMount, onDestroy } from "svelte";
  import { api } from "../lib/api";
  import { createAttachSocket, sendStdin } from "../lib/ws";
  import type { WsMessage } from "../lib/ws";

  interface Props {
    ns: string;
    name: string;
    container: string;
    onDisconnect?: () => void;
  }

  let { ns, name, container, onDisconnect }: Props = $props();

  let terminalEl: HTMLDivElement;
  let term: any;
  let fitAddon: any;
  let socket: WebSocket | null = null;
  let resizeObserver: ResizeObserver;
  let status = $state<"loading" | "history" | "connecting" | "connected" | "disconnected">("loading");

  onMount(() => {
    init();
  });

  async function init() {
    const [{ Terminal }, { FitAddon }] = await Promise.all([
      import("@xterm/xterm"),
      import("@xterm/addon-fit"),
    ]);
    await import("@xterm/xterm/css/xterm.css");

    term = new Terminal({
      cursorBlink: true,
      fontSize: 13,
      fontFamily: "var(--font-mono)",
      scrollback: 5000,
      theme: {
        background: "#0f1117",
        foreground: "#e1e4ed",
        cursor: "#6c8cff",
        selectionBackground: "#2e3346",
      },
    });

    fitAddon = new FitAddon();
    term.loadAddon(fitAddon);
    term.open(terminalEl);
    fitAddon.fit();

    // Step 1: Load log history
    status = "history";
    try {
      const { lines } = await api.getLogHistory(ns, name, 1000, container);
      if (lines) {
        // Write history with a dim style, then reset
        term.write("\x1b[2m"); // dim
        term.write(lines.replace(/\n/g, "\r\n"));
        term.write("\x1b[0m"); // reset
      }
    } catch {
      // silently skip history
    }

    // Step 2: Connect attach WebSocket
    status = "connecting";
    connectAttach();

    // Handle resize
    resizeObserver = new ResizeObserver(() => {
      fitAddon?.fit();
    });
    resizeObserver.observe(terminalEl);
  }

  function connectAttach() {
    socket = createAttachSocket(ns, name, container);

    socket.onopen = () => {
      status = "connected";
    };

    socket.onmessage = (event) => {
      try {
        const msg: WsMessage = JSON.parse(event.data);
        if (msg.type === "stdout" || msg.type === "stderr") {
          const bytes = atob(msg.data);
          term.write(bytes);
        }
      } catch {
        term.write(event.data);
      }
    };

    socket.onclose = () => {
      status = "disconnected";
      onDisconnect?.();
    };

    socket.onerror = () => {
      status = "disconnected";
    };

    // Forward terminal input to PID 1 stdin
    term.onData((data: string) => {
      if (socket?.readyState === WebSocket.OPEN) {
        sendStdin(socket, data);
      }
    });
  }

  function reconnect() {
    if (socket?.readyState === WebSocket.OPEN) return;
    status = "connecting";
    connectAttach();
  }

  onDestroy(() => {
    resizeObserver?.disconnect();
    socket?.close();
    term?.dispose();
  });
</script>

<div class="terminal-wrapper">
  <div class="terminal-toolbar" class:toolbar-warn={status === "disconnected"}>
    <span class="status-indicator" class:connected={status === "connected"} class:disconnected={status === "disconnected"}></span>
    <span class="status-text">
      {#if status === "loading"}Loading terminal...
      {:else if status === "history"}Loading history...
      {:else if status === "connecting"}Connecting...
      {:else if status === "connected"}Attached to PID 1
      {:else}Disconnected — process may have exited or pod was restarted
      {/if}
    </span>
    {#if status === "disconnected"}
      <button class="btn-reconnect" onclick={reconnect}>Reconnect</button>
    {/if}
    <span class="stdin-note">stdin: true &middot; tty: true</span>
  </div>
  <div class="terminal-container" bind:this={terminalEl}></div>
</div>

<style>
  .terminal-wrapper {
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 4px;
    overflow: hidden;
  }

  .terminal-toolbar {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.35rem 0.75rem;
    background: var(--bg-surface);
    border-bottom: 1px solid var(--border);
    font-size: 0.75rem;
    transition: background 0.2s;
  }

  .terminal-toolbar.toolbar-warn {
    background: rgba(251, 191, 36, 0.08);
    border-bottom-color: var(--warning);
  }

  .status-indicator {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--warning);
    flex-shrink: 0;
  }

  .status-indicator.connected { background: var(--success); }
  .status-indicator.disconnected { background: var(--danger); }

  .status-text {
    color: var(--text-muted);
  }

  .btn-reconnect {
    background: none;
    border: 1px solid var(--border);
    color: var(--accent);
    padding: 0.1rem 0.5rem;
    border-radius: 3px;
    cursor: pointer;
    font-size: 0.7rem;
  }

  .btn-reconnect:hover {
    border-color: var(--accent);
  }

  .stdin-note {
    margin-left: auto;
    color: var(--text-muted);
    opacity: 0.5;
    font-family: var(--font-mono);
    font-size: 0.65rem;
  }

  .terminal-container {
    height: 500px;
    padding: 4px;
  }

  :global(.xterm) {
    padding: 4px;
  }
</style>
